import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as recharts from "recharts";
const { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell, CartesianGrid, Legend, Area, AreaChart } = recharts;

// ============================================================
// GARDEN COFFEE AUDIT SYSTEM v2.0
// С интеграцией Directus, PDF-экспорт, отчёты
// ============================================================

// ============================================================
// DIRECTUS CONFIG — замените на свои данные
// ============================================================
const DIRECTUS_TOKEN = window.__ENV__?.REACT_APP_DIRECTUS_TOKEN || process.env.REACT_APP_DIRECTUS_TOKEN || "";
const DIRECTUS_BASE = "/directus";

// Простой Directus клиент (без зависимости от npm)
const directus = {
  base: DIRECTUS_BASE,
  token: DIRECTUS_TOKEN,
  headers: { "Authorization": `Bearer ${DIRECTUS_TOKEN}`, "Content-Type": "application/json" },

  async query(collection, params = {}) {
    let url = `${this.base}/items/${collection}?`;
    if (params.fields) url += `fields=${params.fields}&`;
    if (params.filter) url += `${params.filter}&`;
    if (params.sort) url += `sort=${params.sort}&`;
    if (params.limit) url += `limit=${params.limit}&`;
    try {
      const res = await fetch(url, { headers: this.headers });
      const json = await res.json();
      return json.data || null;
    } catch { return null; }
  },

  async insert(collection, data) {
    try {
      const res = await fetch(`${this.base}/items/${collection}`, {
        method: "POST", headers: this.headers, body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) { console.error(`Directus INSERT ${collection}:`, json); return null; }
      return Array.isArray(json.data) ? json.data : [json.data];
    } catch (e) { console.error(`Directus INSERT ${collection}:`, e); return null; }
  },

  async uploadFile(file, filename) {
    try {
      const form = new FormData();
      form.append("file", file, filename);
      const res = await fetch(`${this.base}/files`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${this.token}` },
        body: form,
      });
      if (!res.ok) return null;
      const json = await res.json();
      return `${this.base}/assets/${json.data.id}`;
    } catch { return null; }
  },

  async verifyHash(plaintext, hash) {
    try {
      const res = await fetch(`${this.base}/utils/hash/verify`, {
        method: "POST", headers: this.headers,
        body: JSON.stringify({ string: plaintext, hash }),
      });
      const json = await res.json();
      return json.data === true;
    } catch { return false; }
  },
};

// Flag: использовать ли Directus (false = демо-режим с локальными данными)
const USE_DIRECTUS = !!DIRECTUS_TOKEN;

// Сжатие фото до среднего качества
function compressImage(file, maxWidth = 1200, quality = 0.6) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const BRAND = {
  green: "#034E3E", greenLight: "#0A7B5E", greenPale: "#E8F5F0",
  accent: "#D4FF00", accentDark: "#B8E600", // Сделали настоящий НЕОН
  dark: "#0A0A0C", darkCard: "#161618", darkBg: "#0A0A0C", // Сделали цвета как в iOS
  white: "#FFFFFF",
  gray50: "#F5F5F7", gray100: "#F1F3F5", gray200: "#E5E7EB", gray300: "#D1D5DB",
  gray400: "#9CA3AF", gray500: "#6B7280", gray600: "#4B5563", gray700: "#374151", gray800: "#1F2937",
  red: "#EF4444", orange: "#F59E0B", blue: "#3B82F6",
};

// ============================================================
// THEME (Обновленная под стиль SaaS / Каршеринга)
// ============================================================
function tv(dark) {
  return dark ? {
    "--bg":"#0A0A0C","--bg-card":"#161618","--bg-card-hover":"#1C1C1E","--bg-input":"#1C1C1E","--text":"#FFFFFF","--text-sec":"#A1A1A6","--text-mut":"#6B7280",
    "--brd":"rgba(255,255,255,0.05)","--brd-l":"rgba(255,255,255,0.02)","--shd":"0 10px 40px rgba(0,0,0, 0.4)","--brand":BRAND.accent,"--brand-bg":"rgba(212,255,0,0.1)","--brand-tx":BRAND.accent,
    "--ok":"#4ADE80","--err":"#EF4444","--wrn":"#F59E0B","--nav":"rgba(22,22,24,0.85)","--sc-g":"#4ADE80","--sc-m":"#F59E0B","--sc-b":"#EF4444",
  } : {
    "--bg":"#F5F5F7","--bg-card":"#FFFFFF","--bg-card-hover":"#FAFAFC","--bg-input":"#F5F5F7","--text":"#111111","--text-sec":"#8E8E93","--text-mut":"#A1A1A6",
    "--brd":"rgba(0,0,0,0.05)","--brd-l":"rgba(0,0,0,0.02)","--shd":"0 10px 40px rgba(0,0,0, 0.04)","--brand":"#111111","--brand-bg":"#F5F5F7","--brand-tx":"#111111",
    "--ok":"#059669","--err":"#EF4444","--wrn":"#F59E0B","--nav":"rgba(255,255,255,0.85)","--sc-g":"#059669","--sc-m":"#F59E0B","--sc-b":"#EF4444",
  };
}

const COFFEE_SHOPS = [
  { id: 1, name: "Калинка", city: "Тюмень" },
  { id: 2, name: "Парус", city: "Тюмень" },
  { id: 3, name: "Мельникайте", city: "Тюмень" },
  { id: 4, name: "Океан", city: "Тюмень" },
  { id: 5, name: "Советская", city: "Тюмень" },
  { id: 6, name: "Арсиб", city: "Тюмень" },
  { id: 7, name: "Панорама", city: "Тюмень" },
  { id: 8, name: "Газпром", city: "Тюмень" },
  { id: 9, name: "Новин", city: "Тюмень" },
  { id: 10, name: "Преображенский", city: "Тюмень" },
  { id: 11, name: "Европейский", city: "Тюмень" },
  { id: 12, name: "Свердлова", city: "Тюмень" },
  { id: 13, name: "Драмтеатр", city: "Тюмень" },
  { id: 14, name: "Видный", city: "Тюмень" },
  { id: 15, name: "Осипенко", city: "Тюмень" },
  { id: 16, name: "Сургут", city: "Сургут" },
  { id: 17, name: "Гагарина", city: "Екатеринбург" },
  { id: 18, name: "Жемчужина", city: "Тобольск" },
];

const USERS = [
  { id: 1, login: "Sergey", password: "AuditGarden2026", name: "Сергей", role: "admin" },
  { id: 2, login: "Guzel", password: "AuditGarden2026", name: "Гузель", role: "admin" },
  { id: 3, login: "Anna", password: "AuditGarden2026", name: "Анна", role: "auditor" },
  { id: 4, login: "Ksenia", password: "AuditGarden2026", name: "Ксения", role: "auditor" },
];

const AUDIT_SECTIONS = [
  {
    id: "service", title: "1. Сервис",
    items: [
      { id: "1.1", question: "Громко и бодро поприветствовали Вас, когда Вы вошли?", type: "yesno" },
      { id: "1.2", question: "Сотрудники поддерживают контакт с очередью (здороваются с гостями)?", type: "yesno" },
      { id: "1.3", question: "Была ли сопутствующая фраза при отдаче заказа?", type: "yesno" },
      { id: "1.4", question: "Громко ли попрощались, когда Вы уходили?", type: "yesno" },
    ]
  },
  {
    id: "sales", title: "2. Продажи",
    items: [
      { id: "2.1", question: "Было ли дополнительное предложение к блюду или напитку?", type: "yesno" },
      { id: "2.2", question: "Время приготовления блюда (мин)", type: "minutes", threshold: 25 },
      { id: "2.3", question: "Время приготовления напитка (мин)", type: "minutes", threshold: 9 },
    ]
  },
  {
    id: "hygiene", title: "3. Опрятность, гигиена и чистота",
    items: [
      { id: "3.1", question: "Выглядел ли сотрудник аккуратно? (фартук, волосы, ногти)", type: "yesno" },
      { id: "3.2", question: "У сотрудника в наличии бейдж?", type: "yesno" },
      { id: "3.3", question: "Время уборки грязной посуды со столов (мин)", type: "minutes", threshold: 6 },
      { id: "3.4", question: "Все ли блюда/напитки/ингредиенты были в наличии?", type: "yesno" },
      { id: "3.5", question: "Чисто ли в туалетной комнате?", type: "yesno" },
      { id: "3.6", question: "Чистая ли входная группа?", type: "yesno" },
      { id: "3.7", question: "Чисто ли в зале?", type: "yesno" },
    ]
  },
  {
    id: "comfort", title: "4. Эмоции / комфорт",
    items: [
      { id: "4.1", question: "Комфортно ли в зале? (музыка, температура, запах)", type: "yesno" },
      { id: "4.2", question: "Возникло ли ощущение гостеприимства от сотрудников?", type: "yesno" },
      { id: "4.3", question: "Всё ли оборудование работает? (вывеска, терминал, подсветка)", type: "yesno" },
    ]
  },
  {
    id: "impression", title: "5. Ваше впечатление",
    items: [
      { id: "5.1", question: "Оценка баристы по качеству обслуживания", type: "rating" },
      { id: "5.2", question: "Оценка скорости обслуживания", type: "rating" },
      { id: "5.3", question: "Оценка вкуса и качества напитка", type: "rating" },
      { id: "5.4", question: "Оценка вкуса и качества блюда", type: "rating" },
      { id: "5.5", question: "Оценка внешнего вида и качества подачи", type: "rating" },
      { id: "5.6", question: "Что особенно понравилось?", type: "text" },
      { id: "5.7", question: "Что не понравилось? Что могло быть лучше?", type: "text" },
    ]
  }
];

const ALL_QUESTIONS = {};
AUDIT_SECTIONS.forEach(s => s.items.forEach(i => { ALL_QUESTIONS[i.id] = i; }));

function generateDemoData() {
  const audits = [];
  const now = new Date();
  for (let i = 0; i < 120; i++) {
    const date = new Date(now); date.setDate(date.getDate() - Math.floor(Math.random() * 120));
    const shop = COFFEE_SHOPS[Math.floor(Math.random() * COFFEE_SHOPS.length)];
    const user = USERS[Math.floor(Math.random() * USERS.length)];
    const baristaNames = ["Анна","Мария","Олег","Дмитрий","Елена","Ксения","Алексей","Наталья","Ирина","Павел"];
    const drinks = ["Капучино","Латте","Американо","Раф","Флэт Уайт","Эспрессо","Мокко","V60","Матча Латте"];
    const foods = ["Круассан","Сэндвич","Чизкейк","Панини","Салат Цезарь","Блинчики","Тост с авокадо","Маффин"];
    const answers = {}, comments = {};
    AUDIT_SECTIONS.forEach(section => {
      section.items.forEach(item => {
        if (item.type === "yesno") { const r = Math.random(); answers[item.id] = r > 0.18 ? "yes" : r > 0.04 ? "no" : "na"; if (answers[item.id] === "no") comments[item.id] = "Замечание по данному пункту"; }
        else if (item.type === "minutes") { answers[item.id] = Math.floor(Math.random() * (item.threshold + 10)) + 1; if (answers[item.id] > item.threshold) comments[item.id] = "Превышено допустимое время"; }
        else if (item.type === "rating") { answers[item.id] = Math.floor(Math.random() * 3) + 3; }
        else if (item.type === "text") { answers[item.id] = item.id === "5.6" ? "Приятная атмосфера, вкусный кофе, улыбчивый бариста" : "Можно улучшить скорость обслуживания в часы пик"; }
      });
    });
    let scored = 0, total = 0;
    AUDIT_SECTIONS.slice(0, 4).forEach(s => { s.items.forEach(it => { const v = answers[it.id]; if (v === "na" || v === undefined) return; total++; if (it.type === "yesno" && v === "yes") scored++; if (it.type === "minutes" && v <= it.threshold) scored++; }); });
    audits.push({
      id: `audit-${i}`, shopId: shop.id, shopName: shop.name, city: shop.city,
      userId: user.id, userName: user.name, baristaName: baristaNames[Math.floor(Math.random() * baristaNames.length)],
      drink: drinks[Math.floor(Math.random() * drinks.length)], food: foods[Math.floor(Math.random() * foods.length)],
      date: date.toISOString(), answers, comments, score: total > 0 ? Math.round((scored / total) * 100) : 0, totalPoints: total, scoredPoints: scored,
    });
  }
  return audits.sort((a, b) => new Date(b.date) - new Date(a.date));
}

// ============================================================
// LOGO
// ============================================================
function GardenLogo({ color = BRAND.green, height = 28 }) {
  return (
    <svg viewBox="0 0 840 135.84" height={height} fill={color}>
      <g><path d="M703.74,119.69c1.23,8.85-12.33,3.74-17.5,3.19-33.27-3.55-50.46,5.92-54.54,8.57-.67.44-1.45.67-2.25.67h-36.97c-2.28,0-4.14-1.85-4.14-4.14V7.77c0-2.28,1.85-4.14,4.14-4.14h37.56c.94,0,1.84.32,2.58.9,3.45,2.74,14.74,10.74,28.33,10.24,8.76-.33,14.5.86,14.94,5.09.57,5.52-5.1,4.23-9.6,3.49-16.04-2.66-27.2.86-36.17,5.1,13.3,16.02,36,17.57,42.68,17.48,8.46-.12,11.61,1.77,12.06,5.29.46,3.64-2.27,5.21-7.46,4.4-31.81-4.94-45.19,5.32-45.19,5.32,0,0,12.59,18.61,49.42,18.31,13.51-.11,13.77,4.17,13.24,6.78-1.11,5.48-11.06,2.61-14.39,2.18-31.57-4.08-44.91,6.1-48.64,8.29,16.57,16.41,31.67,17.33,56.86,17.49,9.96.07,14.54,2,15.05,5.7Z"/><path d="M840,7.77v120.22c0,2.28-1.85,4.14-4.14,4.14h-85.48c24.07-8.91,28.92-35.91,28.92-35.91-17.24-2.74-35.02,2.35-47.57,13.69-4.69,4.24-10.88,13.54-15.9,8.03-5.42-5.95,2.82-11.99,8.97-14.68,36.26-15.88,35.84-36.22,35.84-39.73-5.87-2.41-29.13-1.64-43.79,14.9-1.51,1.71-7.27,9.22-12.09,5.94-1.92-1.31-5.06-8.76,5.9-13.48,30.12-12.99,34.43-29.76,34.37-36.28-7.45-2.88-26.19-.78-39.12,11.63-1.28,1.23-8.66,8.3-12.32,3.74-2.91-3.63-1.41-9.37,8.53-12.7,29.61-9.93,29.3-29.45,29.3-29.45,0,0-13.4-1.38-25.32,2.35-6.37,2-15.76,9.78-17.51,10.56-3.82,1.7-5.91-1.47-5.35-4.56.63-3.5,3.39-5.43,6.63-6.34,6.65-1.86,12.16-4.49,14.38-5.62.71-.36,1.5-.55,2.3-.55,7.45,0,45.96,0,54,0,1.65,0,3.13.97,3.79,2.48l26.46,60.74c.31.72,1.02,1.18,1.81,1.18h0c1.09,0,1.97-.88,1.97-1.97V7.77c0-2.28,1.85-4.14,4.14-4.14h37.14c2.28,0,4.14,1.85,4.14,4.14Z"/></g>
      <path fillRule="evenodd" d="M383.86,129.78l-16.37-33.96h-17.46v32.17c0,2.28-1.85,4.14-4.14,4.14h-36.02c-2.28,0-4.14-1.85-4.14-4.14V7.8c0-2.28,1.85-4.14,4.14-4.14h61.46c37.43,0,54.87,14.68,54.87,46.2,0,16.83-5.02,28.66-15.34,36.19l-2.68,1.95,20.4,38.02c1.48,2.76-.52,6.09-3.64,6.09h-37.37c-1.59,0-3.04-.91-3.73-2.34ZM350.03,64.54h16.29c11.26,0,16.97-4.94,16.97-14.69,0-6.81-2.94-14.92-16.97-14.92h-16.29v29.61Z"/>
      <path fillRule="evenodd" d="M72.97,135.84C30.01,135.84,0,107.91,0,67.92S30.01,0,72.97,0c33.53,0,60.24,19.95,67.01,47.19.65,2.62-1.31,5.16-4.01,5.16h-38.28c-1.24,0-2.43-.54-3.2-1.51-4.26-5.4-11.42-9.47-19.97-9.47-14.82,0-25.57,11.16-25.57,26.55s7.88,28.78,25.48,29.22h.97c9.79.01,15.34-7.64,16.37-9.19.12-.17.17-.37.17-.58h0c0-.59-.48-1.07-1.07-1.07h-9.6c-2.28,0-4.14-1.85-4.14-4.14v-11.78c0-2.28,1.85-4.14,4.14-4.14h56.06c2.28,0,4.14,1.85,4.14,4.14v57.62c0,2.28-1.85,4.14-4.14,4.14h-23.03c-2.28,0-4.14-1.85-4.14-4.14v-6.77c0-.5-.61-.76-.96-.39-3.44,3.57-16.01,14.99-36.24,14.99Z"/>
      <path fillRule="evenodd" d="M248.99,128.93l-4.28-18.34h-43.03l-4.28,18.34c-.44,1.87-2.11,3.2-4.03,3.2h-38.65c-2.77,0-4.76-2.67-3.96-5.33L186.86,6.61c.53-1.75,2.14-2.95,3.96-2.95h64.74c1.83,0,3.44,1.2,3.96,2.95l36.1,120.19c.8,2.65-1.19,5.33-3.96,5.33h-38.65c-1.92,0-3.59-1.32-4.03-3.2ZM209.28,77.56h27.84l-8.15-35.23h-11.53l-8.15,35.23Z"/>
      <path fillRule="evenodd" d="M443.24,127.99V7.8c0-2.28,1.85-4.14,4.14-4.14h65.88c42.8,0,67.35,23.41,67.35,64.23s-23.92,64.23-67.35,64.23h-65.88c-2.28,0-4.14-1.85-4.14-4.14ZM487.54,93.08h18.57c17.85,0,28.08-9.19,28.08-25.19s-10.23-25.19-28.08-25.19h-18.57v50.38Z"/>
    </svg>
  );
}

// ============================================================
// ICONS
// ============================================================
const I = {
  sun: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  moon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  home: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  clipboard: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>,
  chart: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  history: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  logout: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  check: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  chevRight: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
  send: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  download: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  alert: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  trophy: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>,
  pdf: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  share: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  barista: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  coffee: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
  camera: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  trash: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
};

// ============================================================
// UTILS
// ============================================================
function calcScore(answers) {
  let s = 0, t = 0;
  AUDIT_SECTIONS.slice(0, 4).forEach(sec => sec.items.forEach(it => {
    const v = answers[it.id]; if (v === "na" || v === undefined) return; t++;
    if (it.type === "yesno" && v === "yes") s++;
    if (it.type === "minutes" && v <= it.threshold) s++;
  }));
  return { scored: s, total: t, percent: t > 0 ? Math.round((s / t) * 100) : 0 };
}
function scoreColor(s) { return s >= 85 ? "var(--sc-g)" : s >= 60 ? "var(--sc-m)" : "var(--sc-b)"; }
function fmtDate(d) { return new Date(d).toLocaleDateString("ru-RU",{day:"2-digit",month:"2-digit",year:"numeric"}); }
function fmtDateTime(d) { return new Date(d).toLocaleDateString("ru-RU",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"}); }
function shortName(n) { return n.replace("Garden Coffee ",""); }

// ============================================================
// CIRCULAR PROGRESS
// ============================================================
function Ring({ value, size=80, sw=6 }) {
  const r=(size-sw)/2, c=2*Math.PI*r, off=c-(value/100)*c, col=scoreColor(value);
  return (
    <div style={{position:"relative",width:size,height:size,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
      {/* Добавили filter: drop-shadow для эффекта неона */}
      <svg width={size} height={size} style={{transform:"rotate(-90deg)", filter: `drop-shadow(0px 0px 8px ${col}60)`}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--brd)" strokeWidth={sw}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={sw} strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{transition:"stroke-dashoffset 1s cubic-bezier(0.16, 1, 0.3, 1)"}}/>
      </svg>
      <div style={{position:"absolute",textAlign:"center"}}>
        <div style={{fontSize:size*.28,fontWeight:800,color:"var(--text)"}}>{value}%</div>
      </div>
    </div>
  );
}

function Badge({ score, size="md" }) {
  const col=scoreColor(score);
  const s={sm:{p:"2px 8px",fs:11},md:{p:"3px 10px",fs:13},lg:{p:"4px 14px",fs:15}}[size];
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",padding:s.p,borderRadius:20,background:col+"18",color:col,fontSize:s.fs,fontWeight:700}}>{score}%</span>;
}

// ============================================================
// PDF EXPORT (generates HTML → opens print dialog)
// ============================================================
function generatePDFReport(audit, type = "single") {
  const w = window.open("", "_blank");
  if (!w) { alert("Разрешите всплывающие окна для экспорта PDF"); return; }
  
  let html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Отчёт Garden Coffee</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Segoe UI',system-ui,sans-serif;color:#1a1a1a;padding:40px;max-width:800px;margin:0 auto}
    .header{display:flex;justify-content:space-between;align-items:center;padding-bottom:20px;border-bottom:3px solid #034E3E;margin-bottom:24px}
    .logo{font-size:24px;font-weight:700;color:#034E3E}
    .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:24px;font-size:13px}
    .meta-item{padding:8px 12px;background:#f5f7f6;border-radius:8px}
    .meta-label{color:#6B7280;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
    .score-box{text-align:center;padding:24px;margin:20px 0;background:#E8F5F0;border-radius:16px}
    .score-num{font-size:48px;font-weight:800;color:#034E3E}
    .section{margin:20px 0}
    .section-title{font-size:15px;font-weight:700;color:#034E3E;padding:8px 0;border-bottom:1px solid #e5e7eb;margin-bottom:8px}
    .item{display:flex;align-items:flex-start;gap:8px;padding:5px 0;font-size:12px;border-bottom:1px solid #f1f3f5}
    .pass{color:#059669;font-weight:700}
    .fail{color:#EF4444;font-weight:700}
    .na{color:#9CA3AF}
    .comment{color:#6B7280;font-style:italic;margin-left:24px;font-size:11px}
    .footer{margin-top:32px;padding-top:16px;border-top:2px solid #034E3E;font-size:11px;color:#6B7280;text-align:center}
    .impression{margin:4px 0;font-size:12px}
    .stars{color:#F59E0B;letter-spacing:2px}
    @media print{body{padding:20px}button{display:none!important}}
  </style></head><body>`;

  if (type === "single" && audit) {
    const shop = COFFEE_SHOPS.find(s => s.id === audit.shopId);
    html += `
    <div class="header"><div class="logo">GARDEN COFFEE</div><div style="text-align:right;font-size:12px;color:#6B7280">Отчёт аудита<br>${fmtDateTime(audit.date)}</div></div>
    <div class="score-box"><div class="score-num">${audit.score}%</div><div style="font-size:14px;color:#034E3E;margin-top:4px">Итоговая оценка · ${audit.scoredPoints}/${audit.totalPoints} баллов</div></div>
    <div class="meta">
      <div class="meta-item"><div class="meta-label">Кофейня</div><div>${audit.shopName}</div></div>
      <div class="meta-item"><div class="meta-label">Город</div><div>${audit.city}</div></div>
      <div class="meta-item"><div class="meta-label">Бариста</div><div>${audit.baristaName}</div></div>
      <div class="meta-item"><div class="meta-label">Проверяющий</div><div>${audit.userName}</div></div>
      <div class="meta-item"><div class="meta-label">Напиток</div><div>${audit.drink}</div></div>
      <div class="meta-item"><div class="meta-label">Блюдо</div><div>${audit.food||"—"}</div></div>
    </div>`;

    AUDIT_SECTIONS.slice(0,4).forEach(sec => {
      html += `<div class="section"><div class="section-title">${sec.title}</div>`;
      sec.items.forEach(it => {
        const v = audit.answers[it.id];
        const cm = audit.comments?.[it.id];
        let status = "", detail = "";
        if (v === "na" || v === undefined) { status = '<span class="na">—</span>'; }
        else if (it.type === "yesno") { status = v === "yes" ? '<span class="pass">✓ Да</span>' : '<span class="fail">✗ Нет</span>'; }
        else if (it.type === "minutes") {
          const ok = v <= it.threshold;
          status = ok ? `<span class="pass">✓ ${v} мин</span>` : `<span class="fail">✗ ${v} мин (макс ${it.threshold})</span>`;
        }
        html += `<div class="item"><div>${status}</div><div style="flex:1">${it.id}. ${it.question}</div></div>`;
        if (cm) html += `<div class="comment">💬 ${cm}</div>`;
      });
      html += `</div>`;
    });

    html += `<div class="section"><div class="section-title">5. Ваше впечатление</div>`;
    AUDIT_SECTIONS[4].items.forEach(it => {
      const v = audit.answers[it.id];
      const photoUrl = audit.photos?.[it.id];
      if (it.type === "rating") {
        const rv = v != null ? v : 0;
        html += `<div class="impression"><span class="stars">${"★".repeat(rv)}${"☆".repeat(5-rv)}</span> ${it.question}: <strong>${v != null ? v : "—"}/5</strong></div>`;
        if (photoUrl) {
          html += `<div style="margin:4px 0 8px"><img src="${photoUrl}" style="width:200px;height:150px;object-fit:cover;border-radius:8px;border:1px solid #e5e7eb" alt="Фото"/></div>`;
        }
      } else if (it.type === "text" && v) {
        html += `<div class="impression" style="margin-top:8px"><strong>${it.question}:</strong><br>${v}</div>`;
      }
    });
    html += `</div>`;
  }

  if (type === "multi" && Array.isArray(audit)) {
    const audits = audit;
    const avg = audits.length > 0 ? Math.round(audits.reduce((s,a)=>s+a.score,0)/audits.length) : 0;
    html += `
    <div class="header"><div class="logo">GARDEN COFFEE</div><div style="text-align:right;font-size:12px;color:#6B7280">Сводный отчёт<br>${fmtDate(new Date())}</div></div>
    <div class="score-box"><div class="score-num">${avg}%</div><div style="font-size:14px;color:#034E3E;margin-top:4px">Средняя оценка · ${audits.length} проверок</div></div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:16px">
    <tr style="background:#034E3E;color:white"><th style="padding:8px;text-align:left">Кофейня</th><th>Дата</th><th>Бариста</th><th>Проверяющий</th><th>Оценка</th></tr>`;
    audits.forEach((a,i) => {
      html += `<tr style="background:${i%2?"#f8fafb":"white"}"><td style="padding:6px 8px">${shortName(a.shopName)}</td><td style="text-align:center">${fmtDate(a.date)}</td><td style="text-align:center">${a.baristaName}</td><td style="text-align:center">${a.userName}</td><td style="text-align:center;font-weight:700;color:${a.score>=85?"#059669":a.score>=60?"#F59E0B":"#EF4444"}">${a.score}%</td></tr>`;
    });
    html += `</table>`;

    // Top errors
    const errs = {};
    audits.forEach(a => { AUDIT_SECTIONS.slice(0,4).forEach(s => s.items.forEach(it => { const v=a.answers[it.id]; if(v==="na"||v===undefined)return; const fail=(it.type==="yesno"&&v==="no")||(it.type==="minutes"&&v>it.threshold); if(fail)errs[it.id]=(errs[it.id]||0)+1; })); });
    const topErrs = Object.entries(errs).sort((a,b)=>b[1]-a[1]).slice(0,5);
    if (topErrs.length) {
      html += `<div class="section"><div class="section-title">ТОП-5 ошибок</div>`;
      topErrs.forEach(([id,cnt],i) => {
        html += `<div class="item"><div style="width:20px;font-weight:700;color:#EF4444">${i+1}</div><div style="flex:1">${id}. ${ALL_QUESTIONS[id]?.question||id}</div><div class="fail">${cnt}×</div></div>`;
      });
      html += `</div>`;
    }
  }

  html += `<div class="footer">Garden Coffee Audit System · ${new Date().getFullYear()} · Сформировано ${fmtDateTime(new Date())}</div>`;
  html += `<script>window.onload=()=>setTimeout(()=>window.print(),500)</script></body></html>`;
  w.document.write(html);
  w.document.close();
}

// ============================================================
// LOGIN
// ============================================================
function LoginScreen({ onLogin, dark }) {
  const [login, setLogin] = useState(""); const [pw, setPw] = useState(""); const [err, setErr] = useState(""); const [show, setShow] = useState(false);
  useEffect(() => { setTimeout(() => setShow(true), 50); }, []);
  const submit = async () => {
    if (USE_DIRECTUS) {
      const res = await directus.query("auditors", { fields: "*", filter: `filter[login][_eq]=${encodeURIComponent(login)}` });
      if (res && res.length > 0) {
        const ok = await directus.verifyHash(pw, res[0].password_hash);
        if (ok) { onLogin({ id: res[0].id, login: res[0].login, name: res[0].name, role: res[0].role }); return; }
      }
    }
    const u = USERS.find(u => u.login === login && u.password === pw);
    if (u) onLogin(u); else setErr("Неверный логин или пароль");
  };
  const inp = { width:"100%",padding:"13px 16px",borderRadius:12,border:`1.5px solid ${dark?"#333":BRAND.gray200}`,background:dark?"#222":BRAND.gray50,color:dark?"#F1F3F5":BRAND.gray800,fontSize:15,outline:"none",boxSizing:"border-box",fontFamily:"'DM Sans',sans-serif" };
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:dark?`linear-gradient(135deg,#0F0F0F,#0A2A20)`:
    `linear-gradient(135deg,${BRAND.greenPale},#D4EDE4,#F5F7F6)`,padding:20,fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{width:"100%",maxWidth:380,padding:"36px 32px",borderRadius:24,background:dark?BRAND.darkCard:BRAND.white,
        boxShadow:dark?"0 24px 64px rgba(0,0,0,.5)":"0 24px 64px rgba(3,78,62,.12)",
        opacity:show?1:0,transform:show?"translateY(0)":"translateY(20px)",transition:"all .6s cubic-bezier(.16,1,.3,1)"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{width:136,height:68,borderRadius:18,margin:"0 auto 16px",background:dark?"rgba(200,230,74,.1)":BRAND.greenPale,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <GardenLogo color={dark?BRAND.accent:BRAND.green} height={20}/>
          </div>
          <h1 style={{fontSize:20,fontWeight:700,color:dark?"#F1F3F5":BRAND.gray800,margin:0,fontFamily:"'DM Sans',sans-serif"}}>Audit System</h1>
          <p style={{fontSize:12,color:dark?"#6B7280":BRAND.gray400,marginTop:4}}>Система аудита кофеен</p>
        </div>
        <div style={{marginBottom:14}}>
          <label style={{display:"block",fontSize:11,fontWeight:600,color:dark?"#6B7280":BRAND.gray500,marginBottom:5,textTransform:"uppercase",letterSpacing:1}}>Логин</label>
          <input value={login} onChange={e=>{setLogin(e.target.value);setErr("")}} placeholder="director" style={inp}
            onFocus={e=>e.target.style.borderColor=dark?BRAND.accent:BRAND.green} onBlur={e=>e.target.style.borderColor=dark?"#333":BRAND.gray200}/>
        </div>
        <div style={{marginBottom:6}}>
          <label style={{display:"block",fontSize:11,fontWeight:600,color:dark?"#6B7280":BRAND.gray500,marginBottom:5,textTransform:"uppercase",letterSpacing:1}}>Пароль</label>
          <input type="password" value={pw} onChange={e=>{setPw(e.target.value);setErr("")}} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()} style={inp}
            onFocus={e=>e.target.style.borderColor=dark?BRAND.accent:BRAND.green} onBlur={e=>e.target.style.borderColor=dark?"#333":BRAND.gray200}/>
        </div>
        {err && <p style={{color:BRAND.red,fontSize:13,margin:"8px 0 0",textAlign:"center"}}>{err}</p>}
        <button onClick={submit} style={{width:"100%",padding:14,borderRadius:14,border:"none",marginTop:20,background:dark?BRAND.accent:BRAND.green,color:dark?BRAND.dark:BRAND.white,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Войти</button>
        <p style={{fontSize:10,color:dark?"#4B5563":BRAND.gray400,textAlign:"center",marginTop:16,lineHeight:1.6}}>
          {USE_DIRECTUS?"Используйте ваш логин и пароль":"Демо: director / garden2024"}
        </p>
      </div>
    </div>
  );
}

// ============================================================
// BOTTOM NAV (mobile)
// ============================================================
function BNav({ active, go }) {
  const items = [{id:"home",icon:I.home,l:"Главная"},{id:"audit",icon:I.clipboard,l:"Аудит"},{id:"analytics",icon:I.chart,l:"Аналитика"},{id:"history",icon:I.history,l:"История"}];
  return (
    <nav style={{position:"fixed",bottom:0,left:0,right:0,zIndex:100,background:"var(--nav)",borderTop:"1px solid var(--brd)",display:"flex",justifyContent:"space-around",padding:"6px 0 env(safe-area-inset-bottom,8px)",backdropFilter:"blur(20px)"}}>
      {items.map(it=>(
        <button key={it.id} onClick={()=>go(it.id)} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,background:"none",border:"none",padding:"6px 14px",cursor:"pointer",color:active===it.id?"var(--brand)":"var(--text-mut)",transition:"color .2s"}}>
          {it.icon}<span style={{fontSize:10,fontWeight:active===it.id?700:500}}>{it.l}</span>
        </button>
      ))}
    </nav>
  );
}

// ============================================================
// SIDEBAR (desktop)
// ============================================================
function Side({ active, go, user, onLogout, dark, toggle }) {
  const items = [{id:"home",icon:I.home,l:"Главная"},{id:"audit",icon:I.clipboard,l:"Новый аудит"},{id:"analytics",icon:I.chart,l:"Аналитика"},{id:"history",icon:I.history,l:"История"}];
  return (
    <aside style={{width:256,minHeight:"100vh",background:"var(--bg-card)",borderRight:"1px solid var(--brd)",display:"flex",flexDirection:"column",position:"fixed",left:0,top:0,bottom:0,zIndex:50}}>
      <div style={{padding:"24px 20px",borderBottom:"1px solid var(--brd)"}}>
        <GardenLogo color={dark?BRAND.accent:BRAND.green} height={22}/>
        <p style={{fontSize:10,color:"var(--text-mut)",marginTop:5,letterSpacing:1.5,textTransform:"uppercase",fontWeight:600}}>Audit System</p>
      </div>
      <div style={{flex:1,padding:"10px 10px"}}>
        {items.map(it=>(
          <button key={it.id} onClick={()=>go(it.id)} style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"11px 14px",borderRadius:12,border:"none",cursor:"pointer",marginBottom:3,transition:"all .15s",background:active===it.id?"var(--brand-bg)":"transparent",color:active===it.id?"var(--brand-tx)":"var(--text-sec)",fontWeight:active===it.id?700:500,fontSize:14,fontFamily:"'DM Sans',sans-serif"}}>
            {it.icon}{it.l}
          </button>
        ))}
      </div>
      <div style={{padding:"14px 12px",borderTop:"1px solid var(--brd)"}}>
        <button onClick={toggle} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 14px",borderRadius:10,border:"none",cursor:"pointer",background:"var(--bg-input)",color:"var(--text-sec)",fontSize:13,fontFamily:"'DM Sans',sans-serif",marginBottom:8}}>
          {dark?I.sun:I.moon}{dark?"Светлая тема":"Тёмная тема"}
        </button>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px"}}>
          <div style={{width:30,height:30,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,background:"var(--brand-bg)",color:"var(--brand-tx)"}}>{user.name[0]}</div>
          <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{user.name}</div></div>
          <button onClick={onLogout} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-mut)",padding:4}}>{I.logout}</button>
        </div>
      </div>
    </aside>
  );
}

// ============================================================
// AUDIT FORM
// ============================================================
function AuditForm({ onSubmit, dark, user }) {
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState({ shopId:"", date:new Date().toISOString().slice(0,16), barista:"", drink:"", food:"" });
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [showCmt, setShowCmt] = useState(null);
  const [photos, setPhotos] = useState({}); // { "5.3": { preview: "data:...", file: Blob }, "5.4": ... }
  const [photoUploading, setPhotoUploading] = useState(null);
  const [done, setDone] = useState(false);
  const [lastAudit, setLastAudit] = useState(null);

  const setA = (id,v) => setAnswers(p=>({...p,[id]:v}));
  const setC = (id,v) => setComments(p=>({...p,[id]:v}));
  const canNext = meta.shopId && meta.barista && meta.drink;
  const totalSteps = 7;
  const progress = ((step+1)/totalSteps)*100;

  const handlePhoto = async (itemId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(itemId);
    try {
      const compressed = await compressImage(file, 1200, 0.6);
      const preview = URL.createObjectURL(compressed);
      setPhotos(p => ({ ...p, [itemId]: { preview, file: compressed } }));
    } catch (err) { console.error("Photo error:", err); }
    setPhotoUploading(null);
  };

  const removePhoto = (itemId) => {
    setPhotos(p => { const n = { ...p }; if (n[itemId]?.preview) URL.revokeObjectURL(n[itemId].preview); delete n[itemId]; return n; });
  };

  const handleSubmit = async () => {
    const shop = COFFEE_SHOPS.find(s=>s.id===parseInt(meta.shopId));
    const {scored,total,percent} = calcScore(answers);
    const photoUrls = {};
    const audit = {
      id:`audit-${Date.now()}`, shopId:shop.id, shopName:shop.name, city:shop.city,
      userId:user.id, userName:user.name, baristaName:meta.barista, drink:meta.drink, food:meta.food,
      date:new Date(meta.date).toISOString(), answers, comments, score:percent, totalPoints:total, scoredPoints:scored, photos: {},
    };

    if (USE_DIRECTUS) {
      const auditRes = await directus.insert("audits", {
        shop_id:shop.id, auditor_id:user.id, barista_name:meta.barista,
        drink:meta.drink, food:meta.food, visit_date:audit.date,
        score_percent:percent, scored_points:scored, total_points:total,
      });

      if (auditRes && auditRes[0]) {
        const auditId = auditRes[0].id;

        // Загружаем фото в Directus Files
        for (const [qId, photoData] of Object.entries(photos)) {
          if (photoData.file) {
            const url = await directus.uploadFile(photoData.file, `${auditId}_${qId}.jpg`);
            if (url) photoUrls[qId] = url;
          }
        }

        const answerRows = [];
        AUDIT_SECTIONS.forEach(sec=>sec.items.forEach(it=>{
          const v = answers[it.id]; if (v===undefined) return;
          const row = {
            audit_id: auditId,
            question_id: it.id,
            answer_type: it.type,
            answer_yesno: null,
            answer_minutes: null,
            answer_rating: null,
            answer_text: null,
            passed: null,
            comment: comments[it.id] || null,
            photo_url: photoUrls[it.id] || null,
          };
          if (it.type==="yesno") { row.answer_yesno=v; row.passed=v==="yes"?true:v==="no"?false:null; }
          else if (it.type==="minutes") { row.answer_minutes=v; row.passed=v<=it.threshold; }
          else if (it.type==="rating") { row.answer_rating=v; }
          else if (it.type==="text") { row.answer_text=v; }
          answerRows.push(row);
        }));
        await directus.insert("audit_answers", answerRows);
      }
    }

    audit.photos = photoUrls;
    onSubmit(audit);
    setLastAudit(audit);
    setDone(true);
  };

  const reset = () => { setStep(0); setMeta({shopId:"",date:new Date().toISOString().slice(0,16),barista:"",drink:"",food:""}); setAnswers({}); setComments({}); setPhotos({}); setDone(false); setLastAudit(null); };

  if (done && lastAudit) {
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",padding:20,textAlign:"center"}}>
        <div style={{width:76,height:76,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--brand-bg)",marginBottom:20}}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={dark?BRAND.accent:BRAND.green} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h2 style={{fontSize:22,fontWeight:700,color:"var(--text)",margin:0,fontFamily:"'DM Sans',sans-serif"}}>Аудит завершён!</h2>
        <p style={{color:"var(--text-sec)",marginTop:6,fontSize:14}}>{lastAudit.shopName}</p>
        <div style={{margin:"16px 0"}}><Ring value={lastAudit.score} size={96} sw={7}/></div>
        <p style={{color:"var(--text-sec)",fontSize:13}}>{lastAudit.scoredPoints} из {lastAudit.totalPoints} баллов</p>
        <div style={{display:"flex",gap:10,marginTop:24,flexWrap:"wrap",justifyContent:"center"}}>
          <button onClick={()=>generatePDFReport(lastAudit,"single")} style={{padding:"11px 20px",borderRadius:12,border:"1.5px solid var(--brd)",background:"transparent",color:"var(--text)",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",display:"flex",alignItems:"center",gap:6}}>
            {I.pdf} Скачать PDF
          </button>
          <button onClick={reset} style={{padding:"11px 24px",borderRadius:12,border:"none",background:"var(--brand)",color:dark?BRAND.dark:BRAND.white,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Новый аудит</button>
        </div>
      </div>
    );
  }

  const inp = {width:"100%",padding:"12px 16px",borderRadius:12,fontSize:15,border:"1.5px solid var(--brd)",background:"var(--bg-input)",color:"var(--text)",outline:"none",boxSizing:"border-box",fontFamily:"'DM Sans',sans-serif"};
  const sel = {...inp,appearance:"none",WebkitAppearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 14px center"};
  const lbl = {display:"block",fontSize:11,fontWeight:600,color:"var(--text-sec)",marginBottom:5,textTransform:"uppercase",letterSpacing:.5};

  const renderMeta = () => (
    <div>
      <h2 style={{fontSize:20,fontWeight:700,color:"var(--text)",margin:"0 0 4px",fontFamily:"'DM Sans',sans-serif"}}>Новая проверка</h2>
      <p style={{color:"var(--text-sec)",fontSize:14,margin:"0 0 22px"}}>Заполните информацию о визите</p>
      <div style={{marginBottom:14}}><label style={lbl}>Кофейня *</label>
        <select value={meta.shopId} onChange={e=>setMeta({...meta,shopId:e.target.value})} style={sel}>
          <option value="">Выберите кофейню</option>
          {["Тюмень","Сургут","Екатеринбург","Тобольск"].map(c=><optgroup key={c} label={c}>{COFFEE_SHOPS.filter(s=>s.city===c).map(s=><option key={s.id} value={s.id}>{shortName(s.name)}</option>)}</optgroup>)}
        </select>
      </div>
      <div style={{marginBottom:14}}><label style={lbl}>Дата и время *</label><input type="datetime-local" value={meta.date} onChange={e=>setMeta({...meta,date:e.target.value})} style={inp}/></div>
      <div style={{marginBottom:14}}><label style={lbl}>Имя бариста *</label><input value={meta.barista} onChange={e=>setMeta({...meta,barista:e.target.value})} placeholder="Имя сотрудника" style={inp}/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div><label style={lbl}>Напиток *</label><input value={meta.drink} onChange={e=>setMeta({...meta,drink:e.target.value})} placeholder="Латте" style={inp}/></div>
        <div><label style={lbl}>Блюдо</label><input value={meta.food} onChange={e=>setMeta({...meta,food:e.target.value})} placeholder="Круассан" style={inp}/></div>
      </div>
    </div>
  );

  const renderSection = (si) => {
    const sec = AUDIT_SECTIONS[si];
    return (
      <div>
        <h2 style={{fontSize:18,fontWeight:700,color:"var(--text)",margin:"0 0 4px",fontFamily:"'DM Sans',sans-serif"}}>{sec.title}</h2>
        <p style={{color:"var(--text-sec)",fontSize:13,margin:"0 0 18px"}}>{si<4?"Каждый пункт = 1 балл":"Оценки и комментарии"}</p>
        {sec.items.map(it=>(
          <div key={it.id} style={{padding:14,borderRadius:14,marginBottom:10,background:"var(--bg-input)",border:"1px solid var(--brd-l)"}}>
            <p style={{fontSize:14,color:"var(--text)",margin:"0 0 10px",lineHeight:1.5,fontWeight:500}}>
              <span style={{color:"var(--brand-tx)",fontWeight:700,marginRight:6}}>{it.id}</span>{it.question}
            </p>
            {it.type==="yesno" && (
              <div style={{display:"flex",gap:8}}>
                {[{v:"yes",l:"Да",c:"var(--ok)"},{v:"no",l:"Нет",c:"var(--err)"},{v:"na",l:"Н/О",c:"var(--text-mut)"}].map(o=>(
                  <button key={o.v} onClick={()=>setA(it.id,o.v)} style={{flex:1,padding:"10px 0",borderRadius:10,border:"2px solid",borderColor:answers[it.id]===o.v?o.c:"var(--brd)",background:answers[it.id]===o.v?o.c+"15":"transparent",color:answers[it.id]===o.v?o.c:"var(--text-sec)",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",transition:"all .15s"}}>{o.l}</button>
                ))}
              </div>
            )}
            {it.type==="minutes" && (
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <input type="number" min="0" max="120" value={answers[it.id]||""} onChange={e=>setA(it.id,parseInt(e.target.value)||0)} placeholder="0" style={{...inp,width:72,textAlign:"center"}}/>
                <span style={{fontSize:13,color:"var(--text-sec)"}}>мин</span>
                {answers[it.id]!==undefined && <span style={{fontSize:11,fontWeight:600,padding:"3px 8px",borderRadius:6,background:answers[it.id]<=it.threshold?"var(--ok)15":"var(--err)15",color:answers[it.id]<=it.threshold?"var(--ok)":"var(--err)"}}>{answers[it.id]<=it.threshold?"Норма":`Макс. ${it.threshold}`}</span>}
              </div>
            )}
            {it.type==="rating" && (
              <div>
                <div style={{display:"flex",gap:6}}>
                  {[1,2,3,4,5].map(r=>(
                    <button key={r} onClick={()=>setA(it.id,r)} style={{width:42,height:42,borderRadius:10,border:"2px solid",borderColor:answers[it.id]===r?"var(--brand)":"var(--brd)",background:answers[it.id]===r?"var(--brand-bg)":"transparent",color:answers[it.id]===r?"var(--brand-tx)":"var(--text-sec)",fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",transition:"all .15s"}}>{r}</button>
                  ))}
                </div>
                {(it.id==="5.3"||it.id==="5.4") && (
                  <div style={{marginTop:10}}>
                    {photos[it.id] ? (
                      <div style={{position:"relative",display:"inline-block",borderRadius:12,overflow:"hidden",border:"2px solid var(--brd)"}}>
                        <img src={photos[it.id].preview} alt="Фото" style={{width:160,height:120,objectFit:"cover",display:"block"}}/>
                        <button onClick={()=>removePhoto(it.id)} style={{position:"absolute",top:4,right:4,width:26,height:26,borderRadius:8,background:"rgba(0,0,0,0.6)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}>
                          {I.trash}
                        </button>
                      </div>
                    ) : (
                      <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:10,border:"1.5px dashed var(--brd)",background:"var(--bg-card)",color:"var(--text-sec)",fontSize:12,fontWeight:600,cursor:"pointer",transition:"all .15s"}}>
                        {photoUploading===it.id ? (
                          <span>Сжатие...</span>
                        ) : (
                          <>{I.camera} Добавить фото</>
                        )}
                        <input type="file" accept="image/*" capture="environment" onChange={e=>handlePhoto(it.id,e)} style={{display:"none"}}/>
                      </label>
                    )}
                  </div>
                )}
              </div>
            )}
            {it.type==="text" && <textarea value={answers[it.id]||""} onChange={e=>setA(it.id,e.target.value)} placeholder="Напишите..." rows={3} style={{...inp,resize:"vertical"}}/>}
            {it.type!=="text" && (
              <div style={{marginTop:6}}>
                {showCmt===it.id ? <textarea value={comments[it.id]||""} onChange={e=>setC(it.id,e.target.value)} placeholder="Комментарий..." rows={2} autoFocus style={{...inp,resize:"vertical",fontSize:13}}/> :
                <button onClick={()=>setShowCmt(it.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"var(--brand-tx)",fontWeight:600,padding:0,fontFamily:"'DM Sans',sans-serif"}}>+ Комментарий</button>}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderReview = () => {
    const {scored,total,percent} = calcScore(answers);
    const shop = COFFEE_SHOPS.find(s=>s.id===parseInt(meta.shopId));
    return (
      <div>
        <h2 style={{fontSize:20,fontWeight:700,color:"var(--text)",margin:"0 0 4px",fontFamily:"'DM Sans',sans-serif"}}>Обзор аудита</h2>
        <p style={{color:"var(--text-sec)",fontSize:14,margin:"0 0 18px"}}>Проверьте данные перед отправкой</p>
        <div style={{textAlign:"center",padding:20,borderRadius:16,background:"var(--bg-input)",marginBottom:18}}>
          <Ring value={percent} size={96} sw={7}/>
          <p style={{fontSize:15,fontWeight:600,color:"var(--text)",marginTop:10}}>{shop?.name}</p>
          <p style={{fontSize:13,color:"var(--text-sec)"}}>{fmtDate(meta.date)} · {meta.barista}</p>
        </div>
        {AUDIT_SECTIONS.slice(0,4).map(sec=>{
          let ss=0,st=0; sec.items.forEach(it=>{const v=answers[it.id];if(v==="na"||v===undefined)return;st++;if(it.type==="yesno"&&v==="yes")ss++;if(it.type==="minutes"&&v<=it.threshold)ss++;});
          return (
            <div key={sec.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",borderBottom:"1px solid var(--brd-l)"}}>
              <span style={{fontSize:14,color:"var(--text)",fontWeight:500}}>{sec.title}</span>
              <span style={{fontSize:14,fontWeight:700,color:scoreColor(st>0?Math.round(ss/st*100):0)}}>{ss}/{st}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{maxWidth:520,margin:"0 auto"}}>
      <div style={{position:"sticky",top:0,zIndex:10,padding:"10px 0",background:"var(--bg)"}}>
        <div style={{height:4,borderRadius:2,background:"var(--brd)",overflow:"hidden"}}>
          <div style={{height:"100%",width:`${progress}%`,background:"var(--brand)",borderRadius:2,transition:"width .3s"}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",marginTop:5}}>
          <span style={{fontSize:11,color:"var(--text-mut)"}}>Шаг {step+1} из {totalSteps}</span>
          <span style={{fontSize:11,color:"var(--text-mut)"}}>{Math.round(progress)}%</span>
        </div>
      </div>
      <div style={{padding:"10px 0 110px"}}>
        {step===0 && renderMeta()}
        {step>=1 && step<=5 && renderSection(step-1)}
        {step===6 && renderReview()}
      </div>
      <div style={{position:"fixed",bottom:56,left:0,right:0,padding:"10px 16px",background:"var(--bg)",borderTop:"1px solid var(--brd-l)",display:"flex",gap:10}}>
        {step>0 && <button onClick={()=>setStep(step-1)} style={{flex:1,padding:13,borderRadius:14,border:"1.5px solid var(--brd)",background:"transparent",color:"var(--text)",fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Назад</button>}
        {step<6 ? (
          <button onClick={()=>setStep(step+1)} disabled={step===0&&!canNext} style={{flex:2,padding:13,borderRadius:14,border:"none",background:(step===0&&!canNext)?"var(--brd)":"var(--brand)",color:(step===0&&!canNext)?"var(--text-mut)":(dark?BRAND.dark:BRAND.white),fontSize:15,fontWeight:700,cursor:(step===0&&!canNext)?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif"}}>Далее</button>
        ) : (
          <button onClick={handleSubmit} style={{flex:2,padding:13,borderRadius:14,border:"none",background:dark?BRAND.accent:BRAND.green,color:dark?BRAND.dark:BRAND.white,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>{I.send} Отправить</button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
function Dashboard({ audits, user, dark, go }) {
  const last30 = audits.filter(a=>new Date(a.date)>new Date(Date.now()-30*864e5));
  const avg = last30.length ? Math.round(last30.reduce((s,a)=>s+a.score,0)/last30.length) : 0;

  const errs = {};
  last30.forEach(a=>{AUDIT_SECTIONS.slice(0,4).forEach(s=>s.items.forEach(it=>{const v=a.answers[it.id];if(v==="na"||v===undefined)return;const fail=(it.type==="yesno"&&v==="no")||(it.type==="minutes"&&v>it.threshold);if(fail)errs[it.id]=(errs[it.id]||0)+1;}));});
  const topErrs = Object.entries(errs).sort((a,b)=>b[1]-a[1]).slice(0,5);

  const shopScores = {};
  last30.forEach(a=>{if(!shopScores[a.shopId])shopScores[a.shopId]={name:a.shopName,scores:[],city:a.city};shopScores[a.shopId].scores.push(a.score);});
  const shopAvgs = Object.entries(shopScores).map(([id,d])=>({id,name:d.name,city:d.city,avg:Math.round(d.scores.reduce((s,v)=>s+v,0)/d.scores.length),count:d.scores.length})).sort((a,b)=>b.avg-a.avg);

  const card = {background:"var(--bg-card)",borderRadius:28,padding:24,border:"1px solid var(--brd-l)",boxShadow:"var(--shd)", transition: "transform 0.2s ease"};

  return (
    <div style={{padding:"0 0 80px"}}>
      <div style={{marginBottom:22}}>
        <h1 style={{fontSize:24,fontWeight:700,color:"var(--text)",margin:0,fontFamily:"'DM Sans',sans-serif"}}>Привет, {user.name}!</h1>
        <p style={{color:"var(--text-sec)",fontSize:14,marginTop:3}}>Обзор за последние 30 дней</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:20}}>
        {[{l:"Средний балл",v:<Ring value={avg} size={60} sw={5}/>},{l:"Проверок",v:<span style={{fontSize:26,fontWeight:700,color:"var(--brand-tx)"}}>{last30.length}</span>},{l:"Кофеен",v:<span style={{fontSize:26,fontWeight:700,color:"var(--brand-tx)"}}>{Object.keys(shopScores).length}</span>},{l:"Всего",v:<span style={{fontSize:26,fontWeight:700,color:"var(--brand-tx)"}}>{audits.length}</span>}].map((c,i)=>(
          <div key={i} style={{...card,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,padding:14}}>
            {c.v}<span style={{fontSize:11,color:"var(--text-mut)",fontWeight:500}}>{c.l}</span>
          </div>
        ))}
      </div>

      {/* Быстрые действия */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
        <button onClick={()=>go("audit")} style={{...card,cursor:"pointer",display:"flex",alignItems:"center",gap:10,padding:14,border:"2px solid var(--brand)",background:"var(--brand-bg)"}}>
          <span style={{color:"var(--brand-tx)"}}>{I.clipboard}</span>
          <span style={{fontSize:14,fontWeight:700,color:"var(--brand-tx)"}}>Новый аудит</span>
        </button>
        <button onClick={()=>generatePDFReport(last30,"multi")} style={{...card,cursor:"pointer",display:"flex",alignItems:"center",gap:10,padding:14}}>
          <span style={{color:"var(--text-sec)"}}>{I.pdf}</span>
          <span style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>Отчёт PDF</span>
        </button>
      </div>

      {/* Top errors */}
      <div style={{...card,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <span style={{color:"var(--err)"}}>{I.alert}</span>
          <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:0}}>ТОП-5 ошибок</h3>
        </div>
        {topErrs.length===0?<p style={{color:"var(--text-mut)",fontSize:13}}>Нет данных</p>:
        topErrs.map(([id,cnt],i)=>(
          <div key={id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:i<topErrs.length-1?"1px solid var(--brd-l)":"none"}}>
            <span style={{width:22,height:22,borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--err)"+"15",color:"var(--err)",fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</span>
            <span style={{flex:1,fontSize:12,color:"var(--text)",lineHeight:1.4}}><strong style={{color:"var(--brand-tx)"}}>{id}</strong> {ALL_QUESTIONS[id]?.question||id}</span>
            <span style={{fontSize:11,fontWeight:700,padding:"2px 6px",borderRadius:6,background:"var(--err)"+"15",color:"var(--err)"}}>{cnt}×</span>
          </div>
        ))}
      </div>

      {/* Shop ranking */}
      <div style={{...card,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <span style={{color:"var(--brand-tx)"}}>{I.trophy}</span>
          <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:0}}>Рейтинг кофеен</h3>
        </div>
        {shopAvgs.slice(0,6).map((s,i)=>(
          <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:i<5?"1px solid var(--brd-l)":"none"}}>
            <span style={{width:22,height:22,borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",background:i<3?"var(--brand-bg)":"var(--bg-input)",color:i<3?"var(--brand-tx)":"var(--text-mut)",fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</span>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{shortName(s.name)}</div><div style={{fontSize:11,color:"var(--text-mut)"}}>{s.city} · {s.count} пр.</div></div>
            <Badge score={s.avg} size="sm"/>
          </div>
        ))}
      </div>

      {/* Recent */}
      <div style={card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:0}}>Последние проверки</h3>
          <button onClick={()=>go("history")} style={{background:"none",border:"none",cursor:"pointer",color:"var(--brand-tx)",fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Все →</button>
        </div>
        {audits.slice(0,5).map((a,i)=>(
          <div key={a.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:i<4?"1px solid var(--brd-l)":"none"}}>
            <Badge score={a.score} size="sm"/>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{shortName(a.shopName)}</div><div style={{fontSize:11,color:"var(--text-mut)"}}>{fmtDate(a.date)} · {a.userName||user.name}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ANALYTICS
// ============================================================
function Analytics({ audits, dark }) {
  const [shopF, setShopF] = useState("all");
  const [period, setPeriod] = useState("month");
  const pDays = {week:7,month:30,quarter:90,year:365}[period]||30;
  const filtered = audits.filter(a=>{
    const inP = new Date(a.date)>new Date(Date.now()-pDays*864e5);
    const inS = shopF==="all"||a.shopId===parseInt(shopF);
    return inP&&inS;
  });
  const avg = filtered.length?Math.round(filtered.reduce((s,a)=>s+a.score,0)/filtered.length):0;

  const timeData = useMemo(()=>{
    const bd={};
    filtered.forEach(a=>{const d=fmtDate(a.date);if(!bd[d])bd[d]=[];bd[d].push(a.score);});
    return Object.entries(bd).map(([d,sc])=>({date:d,score:Math.round(sc.reduce((s,v)=>s+v,0)/sc.length)})).sort((a,b)=>{const [da,ma,ya]=a.date.split(".");const [db,mb,yb]=b.date.split(".");return new Date(ya,ma-1,da)-new Date(yb,mb-1,db);});
  },[filtered]);

  const radarData = useMemo(()=>AUDIT_SECTIONS.slice(0,4).map(sec=>{
    let s=0,t=0; filtered.forEach(a=>sec.items.forEach(it=>{const v=a.answers[it.id];if(v==="na"||v===undefined)return;t++;if(it.type==="yesno"&&v==="yes")s++;if(it.type==="minutes"&&v<=it.threshold)s++;}));
    return {section:sec.title.replace(/\d\.\s*/,""),score:t>0?Math.round(s/t*100):0};
  }),[filtered]);

  const shopComp = useMemo(()=>{
    const ss={};filtered.forEach(a=>{if(!ss[a.shopId])ss[a.shopId]={name:shortName(a.shopName),scores:[]};ss[a.shopId].scores.push(a.score);});
    return Object.entries(ss).map(([id,d])=>({name:d.name.length>20?d.name.slice(0,20)+"…":d.name,score:Math.round(d.scores.reduce((s,v)=>s+v,0)/d.scores.length)})).sort((a,b)=>b.score-a.score);
  },[filtered]);

  const impData = useMemo(()=>AUDIT_SECTIONS[4].items.filter(i=>i.type==="rating").map(it=>{
    const vals=filtered.map(a=>a.answers[it.id]).filter(v=>v!==undefined);
    return {name:it.question.replace(/Оценка\s*/,"").slice(0,22),avg:vals.length?+(vals.reduce((s,v)=>s+v,0)/vals.length).toFixed(1):0};
  }),[filtered]);

  // Barista ranking
  const baristaData = useMemo(()=>{
    const bs={};filtered.forEach(a=>{if(!bs[a.baristaName])bs[a.baristaName]={scores:[],shops:new Set()};bs[a.baristaName].scores.push(a.score);bs[a.baristaName].shops.add(shortName(a.shopName));});
    return Object.entries(bs).map(([name,d])=>({name,shops:[...d.shops].join(", "),avg:Math.round(d.scores.reduce((s,v)=>s+v,0)/d.scores.length),count:d.scores.length})).sort((a,b)=>b.avg-a.avg).slice(0,8);
  },[filtered]);

  const cc = dark?{text:"#9CA3AF",grid:"#2A2A2A"}:{text:"#6B7280",grid:"#E5E7EB"};
  const card = {background:"var(--bg-card)",borderRadius:28,padding:24,border:"1px solid var(--brd-l)",boxShadow:"var(--shd)", transition: "transform 0.2s ease"};
  const selSt = {padding:"8px 30px 8px 12px",borderRadius:10,fontSize:13,fontWeight:600,border:"1.5px solid var(--brd)",background:"var(--bg-input)",color:"var(--text)",fontFamily:"'DM Sans',sans-serif",outline:"none",appearance:"none",WebkitAppearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 10px center"};

  return (
    <div style={{padding:"0 0 80px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10,marginBottom:22}}>
        <div>
          <h1 style={{fontSize:24,fontWeight:700,color:"var(--text)",margin:0,fontFamily:"'DM Sans',sans-serif"}}>Аналитика</h1>
          <p style={{color:"var(--text-sec)",fontSize:14,marginTop:3}}>{filtered.length} проверок</p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <button onClick={()=>generatePDFReport(filtered,"multi")} style={{...selSt,cursor:"pointer",display:"flex",alignItems:"center",gap:4,background:"var(--brand-bg)",color:"var(--brand-tx)",borderColor:"var(--brand)"}}>
            {I.download} PDF
          </button>
          <select value={period} onChange={e=>setPeriod(e.target.value)} style={selSt}>
            <option value="week">Неделя</option><option value="month">Месяц</option><option value="quarter">Квартал</option><option value="year">Год</option>
          </select>
          <select value={shopF} onChange={e=>setShopF(e.target.value)} style={{...selSt,maxWidth:180}}>
            <option value="all">Все кофейни</option>
            {COFFEE_SHOPS.map(s=><option key={s.id} value={s.id}>{shortName(s.name)}</option>)}
          </select>
        </div>
      </div>

      {/* Summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:20}}>
        <div style={{...card,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",marginBottom:0,textAlign:"center"}}>
          <Ring value={avg} size={66} sw={5}/>
          <p style={{fontSize:11,color:"var(--text-mut)",marginTop:6}}>Средний балл</p>
        </div>
        {radarData.map(d=>
          <div key={d.section} style={{...card,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",marginBottom:0,textAlign:"center"}}>
            <div style={{fontSize:26,fontWeight:700,color:scoreColor(d.score)}}>{d.score}%</div>
            <p style={{fontSize:10,color:"var(--text-mut)",marginTop:2,lineHeight:1.3}}>{d.section}</p>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div style={{...card, marginBottom: 16}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:"0 0 14px"}}>Динамика оценок</h3>
        <div style={{height:220}}><ResponsiveContainer width="100%" height="100%">
          <AreaChart data={timeData}><defs><linearGradient id="gfill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={dark?BRAND.accent:BRAND.green} stopOpacity={.3}/><stop offset="95%" stopColor={dark?BRAND.accent:BRAND.green} stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={cc.grid}/><XAxis dataKey="date" tick={{fontSize:10,fill:cc.text}} tickLine={false}/><YAxis domain={[0,100]} tick={{fontSize:10,fill:cc.text}} tickLine={false}/>
            <Tooltip contentStyle={{borderRadius:12,border:"1px solid var(--brd)",background:"var(--bg-card)",fontSize:13,color:"var(--text)"}}/>
            <Area type="monotone" dataKey="score" stroke={dark?BRAND.accent:BRAND.green} strokeWidth={2.5} fill="url(#gfill)" dot={{r:3,fill:dark?BRAND.accent:BRAND.green}} name="Оценка %"/>
          </AreaChart>
        </ResponsiveContainer></div>
      </div>

      {/* Two-column grid for barista + shop comparison */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(320px,1fr))",gap:16,marginBottom:16}}>
        {/* Barista ranking */}
        <div style={{...card,marginBottom:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
            <span style={{color:"var(--brand-tx)"}}>{I.barista}</span>
            <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:0}}>Рейтинг барист</h3>
          </div>
          {baristaData.map((b,i)=>(
            <div key={b.name} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<baristaData.length-1?"1px solid var(--brd-l)":"none"}}>
              <span style={{width:22,height:22,borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",background:i<3?"var(--brand-bg)":"var(--bg-input)",color:i<3?"var(--brand-tx)":"var(--text-mut)",fontSize:11,fontWeight:700,flexShrink:0}}>{i+1}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--text)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.name}</div>
                <div style={{fontSize:10,color:"var(--text-mut)",marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.shops}</div>
              </div>
              <span style={{fontSize:11,color:"var(--text-mut)",flexShrink:0}}>{b.count} пр.</span>
              <Badge score={b.avg} size="sm"/>
            </div>
          ))}
        </div>

        {/* Shop comparison */}
        {shopF==="all" && shopComp.length>0 && (
          <div style={{...card,marginBottom:0}}>
            <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:"0 0 14px"}}>Сравнение кофеен</h3>
            <div style={{height:Math.max(200,shopComp.length*32)}}><ResponsiveContainer width="100%" height="100%">
              <BarChart data={shopComp} layout="vertical" margin={{left:10}}>
                <CartesianGrid strokeDasharray="3 3" stroke={cc.grid} horizontal={false}/><XAxis type="number" domain={[0,100]} tick={{fontSize:10,fill:cc.text}}/>
                <YAxis type="category" dataKey="name" width={120} tick={{fontSize:9,fill:cc.text}}/><Tooltip contentStyle={{borderRadius:12,border:"1px solid var(--brd)",background:"var(--bg-card)",fontSize:13,color:"var(--text)"}}/>
                <Bar dataKey="score" fill={dark?BRAND.accent:BRAND.green} radius={[0,6,6,0]} name="Оценка %"/>
              </BarChart>
            </ResponsiveContainer></div>
          </div>
        )}
      </div>

      {/* Two-column grid for impressions + radar */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(320px,1fr))",gap:16,marginBottom:16}}>
        {/* Impressions */}
        <div style={{...card,marginBottom:0}}>
          <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:"0 0 14px"}}>Средние оценки впечатлений (1-5)</h3>
          <div style={{height:200}}><ResponsiveContainer width="100%" height="100%">
            <BarChart data={impData}><CartesianGrid strokeDasharray="3 3" stroke={cc.grid}/><XAxis dataKey="name" tick={{fontSize:8,fill:cc.text}} tickLine={false}/>
              <YAxis domain={[0,5]} tick={{fontSize:10,fill:cc.text}}/><Tooltip contentStyle={{borderRadius:12,border:"1px solid var(--brd)",background:"var(--bg-card)",fontSize:13,color:"var(--text)"}}/>
              <Bar dataKey="avg" fill={dark?"#60A5FA":BRAND.blue} radius={[6,6,0,0]} name="Средняя"/>
            </BarChart>
          </ResponsiveContainer></div>
        </div>

        {/* Radar */}
        <div style={{...card,marginBottom:0}}>
          <h3 style={{fontSize:15,fontWeight:700,color:"var(--text)",margin:"0 0 14px"}}>Профиль по разделам</h3>
          <div style={{height:260}}><ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}><PolarGrid stroke={cc.grid}/><PolarAngleAxis dataKey="section" tick={{fontSize:10,fill:cc.text}}/><PolarRadiusAxis angle={30} domain={[0,100]} tick={{fontSize:9,fill:cc.text}}/>
              <Radar name="Оценка" dataKey="score" stroke={dark?BRAND.accent:BRAND.green} fill={dark?BRAND.accent:BRAND.green} fillOpacity={.2} strokeWidth={2}/>
            </RadarChart>
          </ResponsiveContainer></div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HISTORY
// ============================================================
function History({ audits, dark }) {
  const [shopF, setShopF] = useState("all");
  const [search, setSearch] = useState("");
  const [expId, setExpId] = useState(null);
  const filtered = audits.filter(a=>{
    if(shopF!=="all"&&a.shopId!==parseInt(shopF))return false;
    if(search){const q=search.toLowerCase();return a.shopName.toLowerCase().includes(q)||a.baristaName.toLowerCase().includes(q)||(a.userName||"").toLowerCase().includes(q);}
    return true;
  });

  const selSt = {padding:"8px 30px 8px 12px",borderRadius:10,fontSize:13,border:"1.5px solid var(--brd)",background:"var(--bg-input)",color:"var(--text)",fontFamily:"'DM Sans',sans-serif",outline:"none",fontWeight:600,appearance:"none",WebkitAppearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 10px center"};

  return (
    <div style={{padding:"0 0 80px"}}>
      <h1 style={{fontSize:24,fontWeight:700,color:"var(--text)",margin:"0 0 16px",fontFamily:"'DM Sans',sans-serif"}}>История проверок</h1>
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Поиск..." style={{flex:1,minWidth:140,padding:"8px 14px",borderRadius:10,fontSize:13,border:"1.5px solid var(--brd)",background:"var(--bg-input)",color:"var(--text)",fontFamily:"'DM Sans',sans-serif",outline:"none"}}/>
        <select value={shopF} onChange={e=>setShopF(e.target.value)} style={selSt}><option value="all">Все кофейни</option>{COFFEE_SHOPS.map(s=><option key={s.id} value={s.id}>{shortName(s.name)}</option>)}</select>
      </div>
      <p style={{fontSize:12,color:"var(--text-mut)",marginBottom:10}}>Найдено: {filtered.length}</p>

      {filtered.map(a=>(
        <div key={a.id} style={{background:"var(--bg-card)",borderRadius:14,marginBottom:8,border:"1px solid var(--brd-l)",boxShadow:"var(--shd)",overflow:"hidden"}}>
          <button onClick={()=>setExpId(expId===a.id?null:a.id)} style={{width:"100%",padding:"12px 14px",display:"flex",alignItems:"center",gap:10,background:"none",border:"none",cursor:"pointer",textAlign:"left"}}>
            <Badge score={a.score} size="sm"/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{shortName(a.shopName)}</div>
              <div style={{fontSize:11,color:"var(--text-mut)",marginTop:1}}>{fmtDate(a.date)} · {a.baristaName} · {a.userName||"—"}</div>
            </div>
            <span style={{color:"var(--text-mut)",transform:expId===a.id?"rotate(90deg)":"none",transition:"transform .2s"}}>{I.chevRight}</span>
          </button>
          {expId===a.id && (
            <div style={{padding:"0 14px 14px",borderTop:"1px solid var(--brd-l)"}}>
              <div style={{padding:"10px 0",display:"flex",gap:8,flexWrap:"wrap",fontSize:12}}>
                <span style={{padding:"3px 8px",borderRadius:6,background:"var(--bg-input)",color:"var(--text)"}}>☕ {a.drink}</span>
                {a.food && <span style={{padding:"3px 8px",borderRadius:6,background:"var(--bg-input)",color:"var(--text)"}}>🍽 {a.food}</span>}
                <button onClick={()=>generatePDFReport(a,"single")} style={{padding:"3px 8px",borderRadius:6,background:"var(--brand-bg)",color:"var(--brand-tx)",border:"none",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",display:"flex",alignItems:"center",gap:3}}>
                  {I.pdf} PDF
                </button>
              </div>
              {AUDIT_SECTIONS.slice(0,4).map(sec=>(
                <div key={sec.id} style={{marginBottom:10}}>
                  <h4 style={{fontSize:12,fontWeight:700,color:"var(--brand-tx)",margin:"6px 0 4px"}}>{sec.title}</h4>
                  {sec.items.map(it=>{
                    const v=a.answers[it.id]; const cm=a.comments?.[it.id]; let pass=v==="na"?null:(it.type==="yesno"?v==="yes":v<=it.threshold);
                    return (
                      <div key={it.id} style={{display:"flex",alignItems:"flex-start",gap:6,padding:"3px 0",fontSize:12,lineHeight:1.5}}>
                        {v==="na"||v===undefined?<span style={{color:"var(--text-mut)",fontSize:10}}>—</span>:pass?<span style={{color:"var(--ok)"}}>{I.check}</span>:<span style={{color:"var(--err)"}}>{I.x}</span>}
                        <span style={{color:"var(--text)",flex:1}}>
                          {it.question}{it.type==="minutes"&&v!==undefined&&<span style={{fontWeight:600,marginLeft:4}}>({v} мин)</span>}
                          {cm&&<span style={{color:"var(--text-mut)",fontStyle:"italic",display:"block"}}>💬 {cm}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
              <h4 style={{fontSize:12,fontWeight:700,color:"var(--brand-tx)",margin:"6px 0 4px"}}>5. Впечатление</h4>
              {AUDIT_SECTIONS[4].items.filter(i=>i.type==="rating").map(it=>{
                const val = a.answers[it.id];
                const photoUrl = a.photos?.[it.id];
                return (
                  <div key={it.id} style={{padding:"4px 0",fontSize:12,borderBottom:"1px solid var(--brd-l)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{color:"var(--text)",flex:1}}>{it.question}</span>
                      <span style={{fontWeight:700,color:"var(--brand-tx)",whiteSpace:"nowrap"}}>{val != null ? `${val}/5` : "—"}</span>
                    </div>
                    {val != null && <div style={{color:"#F59E0B",fontSize:13,letterSpacing:2,marginTop:2}}>{"★".repeat(val)}{"☆".repeat(5-val)}</div>}
                    {photoUrl && <img src={photoUrl} alt="Фото" style={{width:120,height:90,objectFit:"cover",borderRadius:8,marginTop:4,border:"1px solid var(--brd)"}}/>}
                  </div>
                );
              })}
              {a.answers["5.6"]&&<p style={{fontSize:12,color:"var(--ok)",marginTop:6}}>👍 {a.answers["5.6"]}</p>}
              {a.answers["5.7"]&&<p style={{fontSize:12,color:"var(--err)"}}>👎 {a.answers["5.7"]}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function GardenAuditApp() {
  const [dark, setDark] = useState(true);
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("home");
  
  // 1. ЗДЕСЬ МЕНЯЕМ НА ПУСТОЙ МАССИВ (убираем генерацию фейков)
  const [audits, setAudits] = useState([]); 
  
  const [mob, setMob] = useState(window.innerWidth<768);

  useEffect(()=>{ const h=()=>setMob(window.innerWidth<768); window.addEventListener("resize",h); return()=>window.removeEventListener("resize",h); },[]);

  // Загрузка данных из Directus
  useEffect(() => {
    // Грузим данные только если пользователь вошел и подключен Directus
    if (!user || !USE_DIRECTUS) return;

    async function fetchRealData() {
      try {
        // Скачиваем аудиты, ответы и список пользователей из базы
        const dbAudits = await directus.query("audits", { fields: "*", sort: "-visit_date", limit: 1000 }) || [];
        const dbAnswers = await directus.query("audit_answers", { fields: "*", limit: 10000 }) || [];
        const dbUsers = await directus.query("auditors", { fields: "id,name" }) || [];

        // Группируем ответы, комментарии и фото по ID аудита
        const answersMap = {};
        const commentsMap = {};
        const photosMap = {};

        dbAnswers.forEach(row => {
          if (!row.audit_id) return;
          if (!answersMap[row.audit_id]) {
            answersMap[row.audit_id] = {};
            commentsMap[row.audit_id] = {};
            photosMap[row.audit_id] = {};
          }
          const qId = row.question_id;
          
          if (row.answer_type === "yesno" && row.answer_yesno != null) answersMap[row.audit_id][qId] = row.answer_yesno;
          else if (row.answer_type === "minutes" && row.answer_minutes != null) answersMap[row.audit_id][qId] = row.answer_minutes;
          else if (row.answer_type === "rating" && row.answer_rating != null) answersMap[row.audit_id][qId] = row.answer_rating;
          else if (row.answer_type === "text" && row.answer_text != null) answersMap[row.audit_id][qId] = row.answer_text;

          if (row.comment) commentsMap[row.audit_id][qId] = row.comment;
          if (row.photo_url) photosMap[row.audit_id][qId] = row.photo_url;
        });

        // Собираем данные в тот формат, который понимают наши графики
        const formattedAudits = dbAudits.map(dbA => {
          const shop = COFFEE_SHOPS.find(s => s.id === dbA.shop_id) || {};
          const dbU = dbUsers.find(u => u.id === dbA.auditor_id);
          const localU = USERS.find(u => u.id === dbA.auditor_id);
          const auditorName = dbU?.name || localU?.name || "Неизвестный";

          return {
            id: dbA.id,
            shopId: dbA.shop_id,
            shopName: shop.name || "Неизвестная кофейня",
            city: shop.city || "",
            userId: dbA.auditor_id,
            userName: auditorName,
            baristaName: dbA.barista_name || "—",
            drink: dbA.drink || "—",
            food: dbA.food || "",
            date: dbA.visit_date,
            score: dbA.score_percent || 0,
            totalPoints: dbA.total_points || 0,
            scoredPoints: dbA.scored_points || 0,
            answers: answersMap[dbA.id] || {},
            comments: commentsMap[dbA.id] || {},
            photos: photosMap[dbA.id] || {}
          };
        });

        // Обновляем приложение настоящими данными
        setAudits(formattedAudits);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      }
    }

    fetchRealData();
  }, [user]);
  // --- КОНЕЦ ДОБАВЛЕННОГО БЛОКА ---

  const handleNew = (audit) => { setAudits(p=>[audit,...p]); };

  if (!user) return <LoginScreen onLogin={setUser} dark={dark}/>;

  const renderPage = () => {
    switch(page) {
      case "home": return <Dashboard audits={audits} user={user} dark={dark} go={setPage}/>;
      case "audit": return <AuditForm onSubmit={handleNew} dark={dark} user={user}/>;
      case "analytics": return <Analytics audits={audits} dark={dark}/>;
      case "history": return <History audits={audits} dark={dark}/>;
      default: return <Dashboard audits={audits} user={user} dark={dark} go={setPage}/>;
    }
  };

  return (
    <div style={{...tv(dark),fontFamily:"'DM Sans',sans-serif",minHeight:"100vh",background:"var(--bg)",transition:"background .3s"}}>
      {!mob && <Side active={page} go={setPage} user={user} onLogout={()=>setUser(null)} dark={dark} toggle={()=>setDark(!dark)}/>}
      <main style={{marginLeft:mob?0:256,padding:mob?"14px 14px 0":"28px 36px 0",maxWidth:mob?"100%":940}}>
        {mob && (
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,padding:"6px 0"}}>
            <GardenLogo color={dark?BRAND.accent:BRAND.green} height={18}/>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <button onClick={()=>setDark(!dark)} style={{width:34,height:34,borderRadius:9,border:"1px solid var(--brd)",background:"var(--bg-card)",color:"var(--text-sec)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{dark?I.sun:I.moon}</button>
              <button onClick={()=>setUser(null)} style={{width:34,height:34,borderRadius:9,border:"1px solid var(--brd)",background:"var(--bg-card)",color:"var(--text-sec)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{I.logout}</button>
            </div>
          </div>
        )}
        {renderPage()}
      </main>
      {mob && <BNav active={page} go={setPage}/>}
      {!USE_DIRECTUS && (
        <div style={{position:"fixed",bottom:mob?60:10,right:10,padding:"6px 12px",borderRadius:8,background:dark?"#333":"#FEF3C7",color:dark?"#F59E0B":"#92400E",fontSize:10,fontWeight:600,zIndex:200,boxShadow:"0 2px 8px rgba(0,0,0,.1)"}}>
          Демо-режим · Directus не подключён
        </div>
      )}
    </div>
  );
}
